import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { GuidedRoomGuest } from "rc-app/Pages/GuidedRoomGuest";
import {
  requestGuidedRoomOtp,
  verifyGuidedRoomOtp,
  guestJoinGuidedRoom,
  getRoomRtcToken,
  getGuidedRoomPreview,
  submitGuidedRoomProfile,
  identifyGuidedRoomMobile,
} from "rc-app/api/guidedRoomActionsApi";
import { getPublicProject } from "rc-app/api/shareLinkActionsApi";
import {
  isRoomGone,
  localPreviewKeyFor,
  classifyGuestRefusal,
  refusalPreview,
  refusalCap,
  MAX_GUEST_PASSES_PER_LEAD,
} from "rc-app/utils/guidedRoomFlow";
import { admittedOnVerify } from "rc-app/utils/guidedRoomGuest";
import { useGuidedRoomVoice } from "rc-app/hooks/useGuidedRoomVoice";
import { useViewportInsets } from "rc-app/hooks/useViewportInsets";
import "./GuidedRoomJoin.css";

/* ── Guided tour join page ─────────────────────────────────────────────
   Route: /visit/:joinCode        (served on visit.openskyvr.com)
          /visit/:joinCode?g=<guest_token>  → family, no OTP

   Public — the buyer has no account. See the auth bypasses in
   RenderOnAuthenticated / RenderOnAnonymous.

   Design: docs/product specs/Guided Tour Join.html + its spec, at the
   locked configuration (horizon scene, night, paper panel). The left
   panel is decorative and marked aria-hidden; everything it says is also
   in the flow for a screen reader.

   ── TWO BACKEND NOTES ──
   1. GET /views/guided_room/join/{join_code} SHIPPED 2026-08-19, so the
      left panel now gets a real project name, agency and host. Every
      piece still degrades independently rather than showing placeholders
      — see `preview` — which stays right for a dead or expired link.
   2. `lobby` is NOT in the public endpoint list, so a waiting participant
      can't poll it. We poll `rtc_token` instead — it's public and it
      requires an ADMITTED session, so a success IS the admission signal.
      Worth replacing with a proper status endpoint later.
*/

/* `details` is the C3 step and runs BEFORE the OTP — the name and email
   ride along with the verification, so the lead row is created complete
   and no placeholder email is ever written.

   `profile` is the same information asked AFTER verification. It only
   runs when the identify check wasn't available and verify_otp came back
   asking for a profile. Two stages for one purpose, because the order is
   the whole point and we can't yet rely on the check existing. */
/* `entering` is the family-pass path. A pass holder was vouched for by a
   verified lead, so they are NOT screened — they go straight in, and this
   stage is the hand-off rather than a queue.

   It still polls, and falls back to the lobby wording if admission hasn't
   come through after a few seconds. Auto-admit is a backend change that
   hasn't shipped; until it does, guests are still queued, and "Taking you
   in…" spinning forever would be a worse lie than "waiting". */
type Stage =
  | "landing" | "details" | "otp" | "profile"
  | "entering" | "lobby" | "admitted" | "ended" | "passfull"
  /* A family link that can't be used for any reason other than being full
     or the tour having ended. Used to be a red inline error under the name
     field; now it gets a calm screen of its own. */
  | "passinvalid";
/* `join_code` is carried on the credential, not just in the URL, because
   the tour screen can be reached without it: /tour/:roomId after a reload
   has a room and nothing else. That screen offers the buyer a link to
   pass on to friends, and the link IS the join code — see the note on
   `joinCode` in GuidedRoomGuest. A pass-door arrival has no join code and
   correctly leaves this undefined. */
type Credential = { session_id: string; session_secret: string; room_id?: string; join_code?: string; invited?: boolean; role?: string };

/* Who skips the lobby — decided by the BACKEND on verify_otp, never here.
   This page is public and has no access to the agency's lead list, so any
   client-side version of this check would be trivially forged.

   The rules (founder, 2026-08-13 — supersedes the 2026-08-06 version):
     C1  invited to THIS room (max 2)  → straight in, no approval
     C2  on the agency's book but NOT invited → lobby, host approves
     C3  unknown number → new lead, collect name + email, then lobby

   Until the backend returns a decision we fall through to WAITING, which
   is today's behaviour — the safe direction to be wrong in. */
const readAdmission = (res: any): { admitted: boolean; needsProfile: boolean } => {
  const status = String(res?.lobby_status || res?.status || "").toUpperCase();
  return {
    admitted: status === "ADMITTED" || res?.auto_admitted === true,
    needsProfile: res?.needs_profile === true || res?.is_new_lead === true,
  };
};

type Preview = {
  /* The join view returns this and always has; it simply went unused
     until the tour started rendering at this URL. Having it here means
     the room id no longer depends on verify_otp echoing it back. */
  room_id?: string;
  project_name?: string;
  agency_name?: string;
  agency_logo_url?: string;
  host_first_name?: string;
  host_name?: string;
  status?: string;        // SCHEDULED | LOBBY_OPEN | LIVE | ENDED
  scheduled_at?: string;
  /* Only meaningful when the URL carries ?g=<guest_token> — the lead who
     forwarded the pass. Lets the guest screen say "Meera invited you"
     instead of an anonymous "You've been invited". */
  invited_by_name?: string;
  /* If the preview returns this, the left panel's location, configuration
     and experience count come from the REAL project via the public
     endpoint — no extra fields needed on the preview itself. */
  project_id?: string;
  /* Fallbacks. If the preview ever returns these directly they win, since
     they'd be cheaper than a second request. Rendered only when present —
     no placeholders, no invented numbers. */
  location?: string;
  configuration?: string;
  /* Whether this room records. Drives the consent notice on every entry
     screen — see recordingNotice below for why it lives on the PUBLIC
     preview rather than the room API. */
  recording_enabled?: boolean;
  experience_count?: number;
  estimated_minutes?: number;
};

const STORE_KEY = "guidedRoom:credential:v1";
const OTP_LEN = 6;
const RESEND_SECONDS = 30;
/* Places on a lead's family pass. ONE link shared with the household, not
   four separate ones — the cap is on joins, enforced server-side. Mirrors
   MAX_GUEST_PASSES_PER_LEAD in the host modal. */
/* Last-resort default only — see the note in GuidedRoomGuest. The
   exhausted response carries the real figure. */
const MAX_FAMILY = 4;

/* Gates the localhost-only preview fallback below. Kept on hostname so
   nothing here can reach stage or prod, where the page degrades instead. */
const IS_LOCAL =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

/* The design's sample preview lived here until 2026-08-19. It existed
   because GET /views/guided_room/join/{join_code} didn't, and it is gone
   now that the endpoint ships: a real code draws real data, and an
   invalid one must look invalid rather than being papered over with a
   villa nobody opened. The localStorage stash below survives it — that
   one is the host's own real room, not sample copy. */

/* "6:30 pm, Tue 12 Aug" — short, unambiguous, no year for a near date. */
const formatWhen = (iso?: string): string => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-IN", {
      hour: "numeric", minute: "2-digit", hour12: true,
      weekday: "short", day: "numeric", month: "short",
    });
  } catch { return ""; }
};

/* "98765 43210" — grouped for readability wherever we echo the number
   back at someone. Anything not ten digits is left exactly as typed. */
const prettyMobile = (raw: string): string => {
  const d = raw.replace(/\D/g, "");
  return d.length === 10 ? `${d.slice(0, 5)} ${d.slice(5)}` : raw;
};

/* Up to two initials. The inviter is usually a first name only, so this
   degrades to a single letter rather than padding it out. */
const initialsOf = (full: string): string =>
  String(full || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const CamIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 10l4.5-2.5v9L15 14" />
    <rect x="3" y="6" width="12" height="12" rx="2" />
  </svg>
);

const TickIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const GuidedRoomJoin: React.FC = () => {
  /* Two doors into the same page:
       /visit/:joinCode          — a lead, verifies a mobile
       /visit/:joinCode?g=<tok>  — family, on a pass
       /pass/:passToken          — family, same thing with a cleaner URL
     The pass token identifies the room by itself, so the /pass/ route
     needs no join code. */
  const { joinCode = "", passToken = "" } = useParams();

  /* Notch insets, and how much of the screen the on-screen keyboard takes.
     Published as CSS variables (--sat/--sab/--kb) and html[data-kb], which is
     what the phone and tablet layers in GuidedRoomJoin.css lay out against.
     See docs/product specs/responsive/RESPONSIVE_SPEC.md sections 2 and 3. */
  useViewportInsets();
  const location = useLocation();
  const guestToken = passToken || new URLSearchParams(location.search).get("g") || "";
  const isGuest = !!guestToken;

  /* What the stored credential is filed under. It CANNOT be the join code
     alone: the `/pass/<token>` door carries no join code, so every family
     link on a phone would share one empty-suffixed key and the second
     tour would restore the first tour's credential — showing a live room
     as "ended" because the poll is asking about the wrong room. Falling
     back to the token keys each pass separately. */
  const credScope = joinCode || guestToken;

  const [stage, setStage] = useState<Stage>("landing");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Lead path
  const [countryCode] = useState("+91");
  const [mobile, setMobile] = useState("");
  /* Fixed-length array, NOT a compacted string. With a string, typing into
     box 4 while 1-3 are empty joins to a 1-character value and the digit
     jumps to the first box. Each slot holds its own digit or "". */
  const [otpArr, setOtpArr] = useState<string[]>(() => Array(OTP_LEN).fill(""));
  const [shake, setShake] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // Guest path — also reused by the new-lead profile step
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [cred, setCred] = useState<Credential | null>(null);

  /* Voice, but only once ADMITTED. Joining the Agora channel from the
     lobby would put a not-yet-approved stranger inside the room's audio
     — the host's screening decision has to gate the microphone as well
     as the door, or the lobby is decorative.

     The buyer joins as a subscriber; the backend decides that from their
     presence record, so there's nothing to enforce here. */
  const [preview, setPreview] = useState<Preview | null>(null);
  /* The family quota, as reported by the backend when a pass is full. */
  const [passMax, setPassMax] = useState<number>(MAX_FAMILY);
  /* The real project behind this tour, fetched from the PUBLIC endpoint
     once the preview tells us which one it is. This is where the left
     panel's location, configuration and experience count actually come
     from — the preview doesn't carry them, and inventing them would put
     the design's sample copy ("Kollur, Hyderabad · 4BHK Villas") in front
     of a real buyer looking at a different project. */
  const [project, setProject] = useState<any>(null);
  /* Sticky: once the backend has told us this is a new lead, the step
     indicator must keep saying "of 4" for the rest of the flow. Letting
     the total shrink again after the profile step would count backwards. */
  const [profileRequired, setProfileRequired] = useState(false);
  /* Set when the identify check says we don't know this number, so the
     details step is inserted before the OTP and the counter reads "of 4". */
  const [detailsRequired, setDetailsRequired] = useState(false);
  /* A pass holder who wasn't let straight in after all — see the poll. */
  const [passQueued, setPassQueued] = useState(false);

  /* Has the host actually started the tour? An INVITED lead comes straight
     in without a lobby, so they can easily arrive before the host does —
     and "You're in" over an empty scene reads as broken.

     Three states, and null matters: `null` means we don't KNOW, which is
     the situation on any host where the preview endpoint isn't live yet.
     Only a positive "the room is not LIVE" shows the waiting screen; not
     knowing degrades to today's behaviour rather than stranding an
     admitted buyer on a spinner forever. */
  const [roomLive, setRoomLive] = useState<boolean | null>(null);

  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  const readRoomLive = (p: any) => {
    const s = String(p?.status || p?.state || "").toUpperCase();
    if (!s) return;                       // no status field — stay unknown
    setRoomLive(s === "LIVE");
  };

  /* Room preview drives the whole left panel. Shipped 2026-08-19, so the
     success path is now the normal one; failure means a dead or expired
     link, and we still degrade rather than error, since a buyer doesn't
     care that a preview call 404'd. */
  /* ── ONE FAILED CALL MUST NOT NAME THE HOME "Your guided tour" ──
     This fetched once. On 2026-09-11 the backend redeployed mid-tour and a
     buyer who opened their link in that window got "Your guided tour ·
     starting soon", no project, no advisor — for as long as they kept the
     tab open, because nothing ever asked again. A 404 is a code the
     backend doesn't know and is final; anything else (a restart, a flaky
     network, a 5xx) is retried with backoff until it answers. */
  useEffect(() => {
    if (!joinCode) return undefined;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    const load = () => getGuidedRoomPreview(joinCode)
      .then((p: any) => {
        if (cancelled || !p) return;
        setPreview(p);
        readRoomLive(p);
        if (String(p.status || "").toUpperCase() === "ENDED") setStage("ended");
      })
      .catch((e: any) => {
        if (cancelled) return;
        if (e?.response?.status !== 404) {
          attempt += 1;
          retryTimer = setTimeout(() => { void load(); }, Math.min(15000, 1000 * 2 ** Math.min(attempt, 4)));
        }
        if (!IS_LOCAL) return;
        /* Localhost fallback: what the host's own browser stashed when it
           created this room — the REAL project, so the panel describes the
           tour you actually opened. It doesn't set roomLive; on a real host
           we must stay "unknown", and inferring live-ness from local
           scaffolding would be the same mistake in a different place.
           Nothing follows it — a code the backend doesn't know now shows
           the degraded panel, which is the truth. */
        try {
          const raw = localStorage.getItem(localPreviewKeyFor(joinCode));
          const stashed = raw ? JSON.parse(raw) : null;
          if (stashed?.project_id) setPreview({ ...stashed, status: "LOBBY_OPEN" });
        } catch { /* degrade — the panel handles a missing preview */ }
      });
    void load();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [joinCode]);

  /* Real project details for the left panel. Public endpoint, no auth —
     the same one the share page uses for anonymous viewers. Silent on
     failure: the panel simply shows less. */
  useEffect(() => {
    const pid = preview?.project_id;
    if (!pid) return undefined;
    let cancelled = false;
    /* await rather than .then — the JSDoc @returns on getPublicProject
       declares the resolved shape, so TypeScript doesn't see a Promise. */
    void (async () => {
      try {
        const p: any = await getPublicProject(pid);
        if (!cancelled && p) setProject(p);
      } catch { /* panel degrades — nothing to tell the buyer */ }
    })();
    return () => { cancelled = true; };
  }, [preview?.project_id]);

  /* Survive a refresh mid-lobby — losing the credential would mean
     re-verifying, and there's no way to recover it server-side.

     localStorage, NOT sessionStorage: the common case on a phone isn't
     F5, it's tapping the same WhatsApp link again, which many mobile
     browsers open as a fresh tab — and a fresh tab gets an empty
     sessionStorage, so the buyer would be re-OTP'd mid-tour.

     This is deliberately NOT the trusted-device skip we turned down
     (§#5 of GUIDED_ROOM_JOIN_OTP_ALIGNMENT.md). That would key off the
     device and admit a stranger opening a FORWARDED link as whoever
     verified there last. This is scoped to one join code, so the blast
     radius is the same room, and it dies with the tour. */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${STORE_KEY}:${credScope}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.session_id && parsed?.session_secret) {
          setCred(parsed);
          /* A pass holder is auto-admitted, so put them back on the
             hand-off rather than in a queue they were never in. Either
             way the poll corrects it on the first tick — this only
             stops them reading "waiting for the host" for 4 seconds. */
          setStage(isGuest ? "entering" : "lobby");
        }
      }
    } catch { /* storage unavailable — start fresh */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credScope]);

  /* Resend countdown. Runs only on the code step; the button is a dead
     link until it hits zero. */
  useEffect(() => {
    if (stage !== "otp" || resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [stage, resendIn]);

  /* Focus the first empty box when the code step appears. */
  useEffect(() => {
    if (stage !== "otp") return;
    const firstEmpty = otpArr.findIndex((d) => !d);
    otpRefs.current[firstEmpty === -1 ? OTP_LEN - 1 : firstEmpty]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const storeCred = (cIn: Credential) => {
    const c: Credential = { ...cIn, ...(joinCode ? { join_code: joinCode } : {}) };
    setCred(c);
    try {
      /* Must match the restore above — localStorage, so a link reopened
         in a new tab doesn't cost the buyer another OTP. */
      localStorage.setItem(`${STORE_KEY}:${credScope}`, JSON.stringify(c));
    } catch { /* non-fatal */ }
  };

  const readCred = (res: any, opts: { role?: string } = {}): Credential | null => {
    const session_id = res?.session_id || res?.session?.session_id;
    const session_secret = res?.session_secret || res?.session?.session_secret;
    if (!session_id || !session_secret) return null;
    return {
      session_id,
      session_secret,
      room_id: res?.room_id || res?.room?.room_id,
      /* ── WHAT THE BUYER LEARNS ABOUT THEMSELVES, ONCE ──
         The backend admits an invited lead immediately and parks everyone
         else in the lobby, so ADMITTED here means "you were on the invite
         list". It is the only statement about their own standing a buyer
         ever receives — `views/guided_room/{id}` and its `/lobby` are
         staff-only — and it is what decides whether they may hand out
         family passes. Recorded at the door because this response is the
         only place it appears. */
      invited: admittedOnVerify(res),
      ...(opts.role ? { role: opts.role } : {}),
    };
  };

  const digits = mobile.replace(/\D/g, "");
  const otpValue = otpArr.join("");
  const otpComplete = otpArr.every((d) => d !== "");
  const clearOtp = () => setOtpArr(Array(OTP_LEN).fill(""));
  /* Both names present and an email that at least has the shape of one.
     Deliberately loose — the only real test of an address is sending to
     it, and a strict pattern rejects valid addresses far more often than
     it catches typos. */
  const detailsValid =
    !!firstName.trim() && !!lastName.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  /* Actually send the code and move to the OTP step. Split out because two
     places do it: a known number straight from step 1, and an unknown one
     after the details step. */
  const requestCode = async (): Promise<boolean> => {
    try {
      await requestGuidedRoomOtp(joinCode, {
        mobile_number: digits,
        country_code: countryCode,
      });
      clearOtp();
      setResendIn(RESEND_SECONDS);
      setStage("otp");
      return true;
    } catch (e: any) {
      if (isRoomGone(e?.response?.status)) { setStage("ended"); return false; }

      /* ── LOCALHOST ONLY ────────────────────────────────────────────────
         A 5xx here means the backend generated and stored an OTP and then
         failed to SEND it — almost always because a developer's machine
         has no Exotel credentials, or a DLT template is mismatched. The
         code is perfectly valid; only the SMS leg is broken.

         In production that must stop the flow: a buyer with no code has
         nowhere to go, and pretending otherwise strands them on a screen
         they can never pass. On localhost it stops US, repeatedly, from
         testing everything that happens AFTER verification — which is
         most of the feature.

         So on localhost we go through to the code screen and say where to
         find the code. Gated the same way the host page gates its lobby
         tier preview, so it can never reach stage or prod. */
      const sendFailed = Number(e?.response?.status) >= 500;
      if (IS_LOCAL && sendFailed) {
        clearOtp();
        setResendIn(RESEND_SECONDS);
        setErr("Local: the SMS didn't send, but the code was generated — read it from the backend log or the Exotel outbox.");
        setStage("otp");
        return true;
      }

      setErr(friendly(e, "Couldn't send the code. Check the number and try again."));
      return false;
    }
  };

  /* Step 1 submit. Ask whether we know this number BEFORE sending a code,
     so an unknown caller gives their details first and the lead record is
     created complete at verification.

     Shipped 2026-08-19 on the local backend. If the check isn't available
     we still fall through to sending the code immediately, and verify_otp's
     `needs_profile` catches new leads afterwards — the page works either
     way, it just asks in a worse order. Keep that fallback: the endpoint
     is confirmed on local only, and stage/prod may not have it yet. */
  const sendOtp = async () => {
    if (digits.length !== 10) { setErr("Enter a valid 10-digit mobile number."); return; }
    setBusy(true); setErr("");
    try {
      let known: boolean | null = null;
      try {
        const res: any = await identifyGuidedRoomMobile(joinCode, {
          mobile_number: digits,
          country_code: countryCode,
        });
        if (typeof res?.known === "boolean") known = res.known;
      } catch (e: any) {
        /* NOT isRoomGone() here, deliberately. A 404 from this call stays
           ambiguous in a way it isn't anywhere else: the endpoint is
           confirmed on the local backend but not on stage or prod, so an
           unrouted path and a dead room still look identical. Reading it
           as "ended" would dead-end EVERY join on an environment that
           lacks the route. 410 is unambiguous — an unimplemented route
           never returns it — so that one still counts. Tighten this to
           isRoomGone() once identify is confirmed live on stage. */
        if (e?.response?.status === 410) { setStage("ended"); setBusy(false); return; }
        /* Anything else, 404 included: unknown-unknown. Carry on as
           before — request_otp is the next call and it reports a dead
           room reliably. */
      }

      if (known === false) {
        setDetailsRequired(true);
        setStage("details");
        setBusy(false);
        return;
      }
      await requestCode();
    } catch (e: any) {
      /* Don't ask someone to re-check a number that's fine — the tour is
         simply over. Same at verify: a link can go stale between the two
         steps if the host ends the room mid-flow. */
      if (isRoomGone(e?.response?.status)) { setStage("ended"); setBusy(false); return; }
      setErr(friendly(e, "Couldn't send the code. Check the number and try again."));
    }
    setBusy(false);
  };

  /* Details step (C3), before the code is sent. Nothing is written yet —
     the record is created at verification, so abandoning here leaves no
     half-made lead behind. */
  const submitDetails = async () => {
    if (!detailsValid) return;
    setBusy(true); setErr("");
    await requestCode();
    setBusy(false);
  };

  const submitOtp = async () => {
    if (!otpComplete) { setErr("Enter the 6-digit code we sent you."); return; }
    setBusy(true); setErr("");
    try {
      const res: any = await verifyGuidedRoomOtp(joinCode, {
        mobile_number: digits,
        country_code: countryCode,
        otp: otpValue,
        /* Collected at the details step and sent WITH the verification, so
           the lead is created complete and no placeholder email is ever
           written. Omitted entirely for a known number — the agency
           already holds these and re-sending risks overwriting good data
           with whatever a stranger typed. */
        ...(detailsRequired ? {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email_id: email.trim(),
        } : {}),
      });
      const c = readCred(res);
      if (!c) throw new Error("Verified, but no session came back.");
      storeCred(c);
      const { admitted, needsProfile } = readAdmission(res);
      /* Profile first even when the backend has already admitted them —
         a nameless row is useless to the host, and this is the only
         moment we can ask. */
      if (needsProfile) { setProfileRequired(true); setStage("profile"); }
      else setStage(admitted ? "admitted" : "lobby");
    } catch (e: any) {
      if (isRoomGone(e?.response?.status)) { setStage("ended"); setBusy(false); return; }
      setErr(friendly(e, "That code didn't match. Check and try again."));
      /* Shake, then clear — retyping into pre-filled boxes is fiddly on a
         phone, and the boxes are the thing that was wrong. */
      setShake(true);
      setTimeout(() => { setShake(false); clearOtp(); otpRefs.current[0]?.focus(); }, 460);
    }
    setBusy(false);
  };

  const submitProfile = async () => {
    if (!firstName.trim()) { setErr("Please tell us your name."); return; }
    setBusy(true); setErr("");
    try {
      await submitGuidedRoomProfile(joinCode, cred as Credential, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
      });
    } catch (e: any) {
      /* A failed profile save must NOT strand someone who has already
         verified — they're a real lead with a valid session either way.
         Let them wait for the host; the host sees the mobile number. */
      if (e?.response?.status !== 404) {
        setErr(friendly(e, "We couldn't save that, but you can still join."));
      }
    }
    setBusy(false);
    setStage("lobby");
  };

  const joinAsGuest = async () => {
    if (!firstName.trim()) { setErr("Tell us your first name so the host knows who's joining."); return; }
    setBusy(true); setErr("");
    try {
      const res: any = await guestJoinGuidedRoom(guestToken, firstName.trim());

      /* ── THE ONLY DESCRIPTION OF THE ROOM A PASS HOLDER EVER GETS ──
         Every public description of a room is keyed on the join code, and
         a /pass/<code> arrival has none — so until the backend added these
         fields (8 September), a family member toured "This project" with
         an empty panel and, worse, was the ONE participant class that
         never saw the recording consent notice. They entered with no
         notice at all.

         Folded into `preview` rather than kept separately because every
         consumer downstream — the panel, the project fetch, the consent
         notice — already reads it from there, and a second source of the
         same truth is how they drift. Merged over, not replaced: on the
         `?g=` door there IS a join code and a real preview already, and
         that one is not worth discarding. */
      const joined = {
        room_id: res?.room_id,
        project_id: res?.project_id,
        project_name: res?.project_name,
        agency_name: res?.agency_name,
        agency_logo_url: res?.agency_logo_url,
        host_first_name: res?.host_first_name,
        recording_enabled: res?.recording_enabled,
      };
      if (Object.values(joined).some((v) => v !== undefined && v !== null)) {
        setPreview((prev: any) => ({ ...(prev || {}), ...joined }));
      }

      /* A guest is never an invited lead, whatever the response says. */
      const c = readCred(res, { role: "guest" });
      if (!c) throw new Error("Joined, but no session came back.");
      storeCred(c);
      /* Straight in, not into a queue — the pass is the approval. */
      setStage("entering");
    } catch (e: any) {
      if (isRoomGone(e?.response?.status)) { setStage("ended"); setBusy(false); return; }
      /* One link shared with the family, capped at four joins — so
         "you're the fifth" is an ordinary outcome, not an error. It gets
         its own screen rather than a form that fails on submit. */
      /* ── EVERY REFUSAL GETS A SCREEN, NONE GETS A RED LINE ──
         The decision lives in classifyGuestRefusal (utils/guidedRoomFlow),
         pulled out and tested after it broke twice in a week and showed a
         fifth relative "That invite link isn't valid any more" in red under
         the name field. That line is gone for good: whatever the reason, the
         person being turned away gets a calm full screen that still names
         the home they came to see. Founder, 2026-09-11 — "it should look
         premium to the user even if we are rejecting him".

         The refusal usually carries the project (item 27). Folded into the
         preview before the stage changes, so the refusal screen renders
         with the name already in place rather than flashing "Your guided
         tour" first. */
      const shown = refusalPreview(e);
      if (shown) setPreview((prev: any) => ({ ...(prev || {}), ...shown }));

      const why = classifyGuestRefusal(e);
      if (why === "gone") { setStage("ended"); setBusy(false); return; }
      if (why === "full") {
        /* Say the number the BACKEND enforced, not one compiled in here. */
        const cap = refusalCap(e);
        if (cap) setPassMax(cap);
        setStage("passfull"); setBusy(false); return;
      }
      setStage("passinvalid"); setBusy(false); return;
    }
    setBusy(false);
  };

  /* ── OTP box behaviour ──────────────────────────────────────────────
     Six inputs backed by one string. Typing advances, backspace on an
     empty box steps back and clears the previous one, and a pasted code
     fills every box at once — people paste the whole thing out of the SMS
     far more often than they type it. */
  const setSlot = (i: number, ch: string) =>
    setOtpArr((prev) => { const next = [...prev]; next[i] = ch; return next; });

  const onOtpChange = (i: number, raw: string) => {
    /* slice(-1) so overtyping a filled box replaces rather than appends. */
    const ch = raw.replace(/\D/g, "").slice(-1);
    setSlot(i, ch);
    if (err) setErr("");
    if (ch && i < OTP_LEN - 1) otpRefs.current[i + 1]?.focus();
  };

  const onOtpKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpArr[i] && i > 0) {
      /* Empty box: step back AND clear, so one press does what people
         expect instead of leaving the previous digit behind. */
      e.preventDefault();
      setSlot(i - 1, "");
      otpRefs.current[i - 1]?.focus();
      return;
    }
    if (e.key === "Enter" && otpComplete) { void submitOtp(); return; }
    if (e.key === "ArrowLeft" && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < OTP_LEN - 1) otpRefs.current[i + 1]?.focus();
  };

  /* People paste the whole code out of the SMS far more often than they
     type it, and a paste lands in whichever box has focus — so fill from
     the start regardless of where it was dropped. */
  const onOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LEN);
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LEN).fill("");
    text.split("").forEach((c, i) => { next[i] = c; });
    setOtpArr(next);
    if (err) setErr("");
    otpRefs.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
  };

  /* Admission poll. rtc_token requires an ADMITTED session, so a success
     means the host has let us in.

     The error is NOT uniformly "keep waiting", and treating it that way
     stranded anyone who opened a link for a finished tour: they'd sit on
     a spinner indefinitely, and a refresh would put them straight back
     because the credential is cached.

       403 / 400 → not admitted yet, genuinely keep waiting
       404 / 410 → the room is gone. Say so and stop.

     Belt and braces: every fifth tick we re-read the public preview,
     which reports ENDED once the host closes the room. That covers a
     backend which keeps returning 403 after a tour ends, and it costs one
     request a minute. It 404s harmlessly until the endpoint ships. */
  const pollRef = useRef<any>(null);
  useEffect(() => {
    if ((stage !== "lobby" && stage !== "entering") || !cred?.room_id) return undefined;
    let stopped = false;
    let ticks = 0;

    const tick = async () => {
      ticks += 1;
      try {
        await getRoomRtcToken(cred.room_id as string, cred);
        if (!stopped) setStage("admitted");
        /* ── THE PASS DOOR HAS NO JOIN CODE ──
           `showTour` needs roomLive === true, and roomLive is only ever set
           from the public preview — which is keyed on the join code. A
           family member arriving at /pass/<code> has none, by design, so
           the preview effect early-returns, roomLive stays null forever,
           and the gate never opens. They land on "You're in — you can hear
           the tour" and stay there for the whole tour: audio, and no room.
           No chat, no people, no walkthrough. Found on a live tour
           2026-09-10, with the guest correctly ADMITTED and visible on
           everyone else's roster.

           This call is the missing signal. rtc_token only succeeds for an
           ADMITTED participant of a room that still exists — a room that
           had ended answers 404/410 and is handled above — so for a pass
           holder, reaching this line IS the proof that the tour is running.
           They could not have been admitted otherwise: somebody had to be
           in the room to admit them.

           Only when there is no join code. A lead who has one keeps
           reading the real status, which can legitimately say the host
           hasn't started yet. */
        if (!stopped && !joinCode) setRoomLive(true);
        return;
      } catch (e: any) {
        const s = e?.response?.status;
        if (s === 404 || s === 410) { if (!stopped) setStage("ended"); return; }
        /* Still not in. For a pass holder that shouldn't happen — they're
           meant to be auto-admitted — so after ~12s stop promising and
           tell the truth: they're waiting for the host after all. This is
           what the page does until the backend ships auto-admit for
           guest passes. */
        if (!stopped && ticks >= 3) setPassQueued(true);
      }
      if (ticks % 5 === 0 && !stopped) {
        try {
          const p: any = await getGuidedRoomPreview(joinCode);
          if (stopped) return;
          readRoomLive(p);
          if (String(p?.status || "").toUpperCase() === "ENDED") setStage("ended");
        } catch { /* endpoint not live yet — ignore */ }
      }
    };

    void tick();
    pollRef.current = setInterval(tick, 4000);
    return () => { stopped = true; clearInterval(pollRef.current); };
  }, [stage, cred, joinCode]);

  /* Admitted, but the host hasn't started yet.
     The lobby poll above stops the moment we're admitted, so without this
     an invited lead who arrived early would sit on "waiting for your
     advisor" with nothing ever telling them he'd turned up.

     Slower than the lobby poll (6s): nobody is being kept waiting on a
     decision here, and it's one public request per person per tour. */
  useEffect(() => {
    if (stage !== "admitted" || roomLive !== false || !joinCode) return undefined;
    let stopped = false;

    const tick = async () => {
      try {
        const p: any = await getGuidedRoomPreview(joinCode);
        if (stopped) return;
        readRoomLive(p);
        if (String(p?.status || "").toUpperCase() === "ENDED") setStage("ended");
      } catch { /* transient or not shipped — try again next tick */ }
    };

    const id = setInterval(tick, 6000);
    return () => { stopped = true; clearInterval(id); };
  }, [stage, roomLive, joinCode]);


  /* ── The tour itself ─────────────────────────────────────────────────
     Once the host is live and we are admitted, the ROOM renders here —
     at this URL. The buyer stays on visit.openskyvr.com/<code> for their
     whole visit: refreshing mid-tour still works, a bookmark stays valid,
     and they never see an internal room id. An earlier version navigated
     to /tour/<roomId>, which worked but moved a buyer to a URL nobody
     ever sent them. See docs/product specs/GUIDED_ROOM_ADMISSION_MATRIX.md.

     LIVE is the trigger, not admission: an invited lead usually arrives
     before the host does, and a room with nothing happening in it reads
     as a broken page. */
  /* Either source will do. The credential carries room_id when verify_otp
     returns it; the join view carries it always. Depending on only the
     first gave a blank screen when a backend response omitted it. */
  const tourRoomId = String(cred?.room_id || preview?.room_id || "");
  const showTour = stage === "admitted" && roomLive === true && !!tourRoomId;

  /* ── Derived copy ───────────────────────────────────────────────────
     Every host-name mention is interpolated. Where we have no name the
     sentence still has to read naturally, hence the "your advisor"
     fallbacks rather than a blank. */
  const hostFirst = preview?.host_first_name || "";
  const hostFull = preview?.host_name || hostFirst;
  const hostLabel = hostFirst || "your advisor";
  const HostLabelCap = hostFirst || "Your advisor";

  /* ── THE RECORDING NOTICE ─────────────────────────────────────────────
     Shown BEFORE anyone enters, on every door into the room, whenever this
     room records.

     Not boilerplate and not a checkbox. Under the DPDP Act a person has to
     be told their voice is being captured before it is, and a line buried
     in terms nobody opened is not being told. A checkbox would satisfy a
     lawyer and cost attendance on a screen whose entire job is getting a
     buyer through it, so this is a notice placed where it is actually
     read — directly above the button that takes them in.

     It says WHY, not just what. "This tour is recorded" reads as
     surveillance; "recorded so Sivaram can follow up on what you asked
     about" is the same fact and the actual reason, and a buyer who
     understands the reason doesn't mind. It also names the chat, because
     messages are stored too and a person told only about audio has not
     been told about that.

     "MAY be recorded", not "is". The flag says the room is capable of it;
     the host decides in the room whether it happens. Claiming a recording
     that never starts is a false statement about someone's own data, and
     the honest wording costs nothing — the point of the notice is that a
     buyer is not surprised later, and "may" covers both outcomes.

     Driven by the room's own setting, carried on the public preview
     (`recording_enabled`) precisely so this screen can ask — it has no
     credential and cannot read the room API. A room that isn't recording
     shows nothing at all rather than a reassurance nobody asked for. */
  /* ── What a buyer needs to know before they hand over a number ──────
     Three things, chosen because each removes a reason not to tap, and
     nothing else — the left column already carries the project, and a
     third reassurance about the number would start to read as something
     being hidden.

       · earphones — voice is the single most common way a tour is spoiled,
         and it costs one line to prevent rather than apologise for;
       · the household — the people who actually decide are usually not the
         one holding the phone, and they can be brought in. Said BEFORE
         joining, because afterwards is too late to fetch anyone;
       · how long — an open-ended commitment is easy to postpone.

     MAX_GUEST_PASSES_PER_LEAD, not a number typed here: raising the cap
     must not leave this screen quietly promising the old one.

     Three cells across rather than three sentences down: at a glance this
     is a specification of what is about to happen, and a buyer skims it
     in the second before they tap. Sentences invited reading; labels
     invite noticing. Each cell is therefore a short bold line and one
     quiet word under it, and the household line loses its explanation of
     the sharing link — that belongs in the room, where the link is. */
  const joinPrep = (
    <ul className="note prep-note">
      <li>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
          <rect x="2.5" y="13.5" width="4.5" height="6.5" rx="1.6" />
          <rect x="17" y="13.5" width="4.5" height="6.5" rx="1.6" />
        </svg>
        <span><b>Use earphones</b>Voice-guided</span>
      </li>
      <li>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="9" cy="8" r="3.2" />
          <path d="M2.5 19a6.5 6.5 0 0 1 13 0" />
          <path d="M16.5 5.6a3.2 3.2 0 0 1 0 6M18 19a6.6 6.6 0 0 0-2.3-5" />
        </svg>
        <span><b>Bring up to {MAX_GUEST_PASSES_PER_LEAD}</b>From your household</span>
      </li>
      <li>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 1.8" />
        </svg>
        <span><b>About 20 min</b>Typical tour</span>
      </li>
    </ul>
  );

  const recordingNotice = preview?.recording_enabled ? (
    <p className="note rec-note">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none" />
      </svg>
      <span>
        This tour may be recorded — voice and chat — so {hostLabel} can follow up on what
        you asked about.
      </span>
    </p>
  ) : null;
  /* `project_name`, not `name` — see the @returns JSDoc on
     getPublicProject. The preview's copy is the fallback. */
  const projectName = project?.project_name || preview?.project_name || "Your guided tour";

  /* Location and configuration, derived from the real project with the
     same field paths ProjectDetailsV3 uses — landmark + city, and the BHK
     list off unit_configurations. A preview-supplied value wins if one
     ever arrives; otherwise this is the only honest source. */
  const projLocation = (() => {
    if (preview?.location) return preview.location;
    const a = project?.address;
    return [a?.landmark, a?.city].filter(Boolean).join(", ");
  })();

  const projConfig = (() => {
    if (preview?.configuration) return preview.configuration;
    const units: any[] = project?.project_type_details?.unit_configurations || [];
    const bhk = Array.from(new Set(
      units
        .map((u: any) => Number(u?.bhk ?? u?.bhk_count ?? u?.configuration ?? NaN))
        .filter((n: number) => Number.isFinite(n) && n > 0),
    )).sort((a: any, b: any) => a - b) as number[];
    if (!bhk.length) return "";
    const raw = String(project?.project_type_details?.type || "").toLowerCase();
    /* Prefix-match, matching V3 — it catches the legacy `commercial_space`
       and won't break if a `villa_standalone` is ever added. */
    const noun = raw.startsWith("villa") ? "Villas"
      : raw.startsWith("commercial") ? "Units"
        : "Apartments";
    const list = bhk.length <= 2
      ? bhk.join(" & ")
      : `${bhk.slice(0, -1).join(", ")} & ${bhk[bhk.length - 1]}`;
    return `${list} BHK ${noun}`;
  })();

  const locLine = [projLocation, projConfig].filter(Boolean).join(" · ");

  /* How many experiences this project actually has. The design's "3" was
     sample copy; this counts what the buyer will really be shown. */
  const experienceCount = preview?.experience_count ?? (project
    ? (Array.isArray(project.meta_models) ? project.meta_models.length : 0)
      + (Array.isArray(project.matterports) ? project.matterports.length : 0)
      /* route_view is a single field, not a list — present means one more
         thing the buyer can be walked through. */
      + (project.route_view ? 1 : 0)
    : 0);
  const statusUp = String(preview?.status || "").toUpperCase();
  const scheduled = formatWhen(preview?.scheduled_at);

  /* Present tense for a pass holder. Unlike a lead's invite, a family pass
     is minted from INSIDE a running tour — "starting soon" would be wrong
     the moment they open it. */
  const eyebrow =
    stage === "ended" || statusUp === "ENDED" ? "Guided tour · ended"
      : statusUp === "LIVE" ? "Guided tour · live now"
        : isGuest ? "Guided tour · in progress"
          : scheduled ? `Guided tour · ${scheduled}`
            /* No preview yet — still loading, or retrying after a failed
               call — is "we don't know", not "starting soon", which told a
               buyer whose tour was already live that it hadn't begun. */
            : !preview ? "Guided tour"
              : "Guided tour · starting soon";

  /* The backend sends the name as the lead record holds it — "arjun",
     "raju" — and a relative's first screen shouldn't open in lowercase. */
  const inviter = (() => {
    const raw = String(preview?.invited_by_name || "").trim();
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
  })();

  /* Step indicator. The total genuinely varies by path — a guest never
     sees an OTP, and a new lead gets an extra details step — so it's
     computed rather than hard-coded to the prototype's 3. */
  const stepList: Stage[] = isGuest
    ? ["landing", "entering"]
    : detailsRequired
      /* C3, the designed order: number → details → code → lobby. */
      ? ["landing", "details", "otp", "lobby"]
      : profileRequired
        /* Fallback order, only when the identify check wasn't available
           and verify_otp asked for a profile afterwards. */
        ? ["landing", "otp", "profile", "lobby"]
        : ["landing", "otp", "lobby"];
  const stageForStep: Stage = stage === "admitted"
    ? (isGuest ? "entering" : "lobby")
    : stage;
  const stepIndex = Math.max(0, stepList.indexOf(stageForStep));
  /* No progress bar on a terminal screen — there's no step to be on. */
  const showSteps = stage !== "ended" && stage !== "passfull";

  /* Gated on `admitted` — see the note by `cred`. The hook tears the
     channel down on its own when this flips false, so an ended tour or a
     removed participant stops hearing the room without anything extra
     here. */
  const voice = useGuidedRoomVoice(
    String(cred?.room_id || ""),
    cred,
    /* Off once the room takes over: it opens its own voice channel with
       this same credential, and two clients on one channel is an echo and
       a wasted publisher slot, not redundancy. */
    stage === "admitted" && !!cred?.room_id && !showTour,
  );

  const paneOn = (s: Stage | "guest") =>
    (s === "guest" ? (stage === "landing" && isGuest) : (stage === s && !(s === "landing" && isGuest)));

  const pane = (s: Stage | "guest") => `pane${paneOn(s) ? " on" : ""}`;

  /* ── EVERY PANE IS IN THE DOM; ONLY ONE IS REACHABLE ──
     The panes all render at once and CSS shows one — that's what makes the
     step transition work, and it stays. But "hidden by CSS" is only hidden
     to someone looking at the screen. The inactive panes' inputs kept
     their place in the focus order, in the accessibility tree and in
     autofill's list of candidates, so on the code screen a browser saw
     six OTP boxes AND two spare name/email forms, one of which is the
     phone field. Found on 2026-09-07 when a script filled "the first
     visible input" and silently wiped the mobile number — a person would
     hit the same thing with Tab or a password manager.

     `inert` is the whole fix: it removes a subtree from focus, the a11y
     tree and hit-testing in one attribute, without touching layout or the
     transition. Passed as "" / undefined because React 18 forwards it as
     a plain HTML attribute rather than a typed prop. */
  const paneProps = (s: Stage | "guest") =>
    ({ className: pane(s), inert: paneOn(s) ? undefined : "" } as any);

  /* The tour itself, at this URL. Placed after every hook on purpose —
     an early return above one of them renders fewer hooks than the
     previous pass and React aborts the tree with a blank screen. */
  /* joinCode is passed explicitly rather than folded into `preview`: it
     comes from the URL, not from the preview response, and pretending
     otherwise would have the next reader looking for a field the backend
     never returns. Empty on the /pass door, which is correct — a family
     guest has no lead link to hand on. */
  if (showTour) {
    return (
      <GuidedRoomGuest
        roomId={tourRoomId}
        preview={preview}
        joinCode={joinCode}
        /* The name they typed on the way in, for the few seconds before their
           own lobby row arrives on the poll. Empty for a known lead, who
           never types one — their name is on the roster instead. */
        selfNameHint={firstName.trim()}
      />
    );
  }

  return (
    <div className="grj-root">
      {/* ── LEFT · the room ───────────────────────────────────────────
          Decorative. Everything it communicates is repeated in the flow
          on the right, so a screen reader loses nothing by skipping it. */}
      <section className="room">
        <div aria-hidden="true">
          <div className="r-sky" />
          <div className="r-haze" />
          <div className="r-band" />
          <div className="r-hz" />
          <span className="r-cloud c2" /><span className="r-cloud c3" />
          <div className="r-birds">
            <svg viewBox="0 0 70 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <path d="M4 8c2-3 4-3 6 0 2-3 4-3 6 0" />
              <path d="M26 4c1.6-2.4 3.2-2.4 4.8 0 1.6-2.4 3.2-2.4 4.8 0" />
              <path d="M48 11c1.3-2 2.6-2 3.9 0 1.3-2 2.6-2 3.9 0" />
            </svg>
          </div>
          <div className="r-ridge far" />
          <div className="r-ridge near" />
          <div className="r-villas">
            <svg viewBox="0 0 800 160" preserveAspectRatio="none" fill="currentColor">
              <path d="M40 160V96l42-30 42 30v64Z" /><path d="M96 160V112l34-24 34 24v48Z" />
              <path d="M210 160V88l48-34 48 34v72Z" /><path d="M286 160V118l30-22 30 22v42Z" />
              <path d="M392 160V104l40-28 40 28v56Z" />
              <path d="M498 160V92l46-32 46 32v68Z" /><path d="M572 160V120l28-20 28 20v40Z" />
              <path d="M664 160V108l38-26 38 26v52Z" />
              <rect className="r-win" x="66" y="112" width="9" height="11" rx="1" />
              <rect className="r-win" x="88" y="112" width="9" height="11" rx="1" />
              <rect className="r-win" x="124" y="128" width="8" height="10" rx="1" />
              <rect className="r-win" x="240" y="106" width="10" height="12" rx="1" />
              <rect className="r-win" x="266" y="106" width="10" height="12" rx="1" />
              <rect className="r-win" x="308" y="134" width="8" height="10" rx="1" />
              <rect className="r-win" x="418" y="120" width="9" height="11" rx="1" />
              <rect className="r-win" x="440" y="120" width="9" height="11" rx="1" />
              <rect className="r-win" x="524" y="110" width="10" height="12" rx="1" />
              <rect className="r-win" x="550" y="110" width="10" height="12" rx="1" />
              <rect className="r-win" x="592" y="134" width="8" height="10" rx="1" />
              <rect className="r-win" x="690" y="124" width="9" height="11" rx="1" />
              <rect className="r-win" x="712" y="124" width="9" height="11" rx="1" />
            </svg>
          </div>
          <div className="r-water" />
          <div className="r-shore" />
          <div className="r-floor" />
          <span className="spark" style={{ left: "18%", bottom: "22%", animationDelay: "0s" }} />
          <span className="spark" style={{ left: "44%", bottom: "16%", animationDelay: "4.6s" }} />
          <span className="spark" style={{ left: "72%", bottom: "20%", animationDelay: "8.8s" }} />
          <span className="spark" style={{ left: "88%", bottom: "14%", animationDelay: "12.1s" }} />
          <div className="r-vig" />
          <div className="r-scrim" />
          <div className="r-grain" />
        </div>

        <div className="r-top">
          <div className="r-brand">
            {preview?.agency_logo_url ? (
              <img className="r-logo" src={preview.agency_logo_url}
                   alt={preview.agency_name || "Agency"} />
            ) : (
              <>
                <span className="r-mark"><CamIcon /></span>
                <span>{preview?.agency_name || "OpenSky"}</span>
              </>
            )}
          </div>
        </div>

        <div className="r-mid">
          <div className="r-eyebrow"><i />{eyebrow}</div>
          <h2 className="r-title">{projectName}</h2>
          {locLine && <div className="r-loc">{locLine}</div>}
          {hostFull && (
            <div className="r-host">
              <span className="r-av">{hostFull.trim().charAt(0).toUpperCase()}</span>
              <div className="r-hinfo">
                {/* "Your guide" for family — they have no advisor
                    relationship, they're along for someone else's tour. */}
                <span className="r-hlbl">{isGuest ? "Your guide" : "Your advisor"}</span>
                <b>{hostFull}</b>
              </div>
            </div>
          )}
        </div>

        {/* Facts render only where we have the number. An invented
            "3 Experiences" on a project with one would be worse than
            showing nothing. */}
        <div className="r-bot">
          {experienceCount > 0 && (
            <div className="r-fact">
              <b>{experienceCount}</b>
              <small>{experienceCount === 1 ? "Experience" : "Experiences"}</small>
            </div>
          )}
          {!!preview?.estimated_minutes && (
            <div className="r-fact">
              <b>~{preview.estimated_minutes} min</b><small>Typical tour</small>
            </div>
          )}
          <div className="r-fact"><b>Live</b><small>Voice guided</small></div>
        </div>
      </section>

      {/* ── RIGHT · verification ──────────────────────────────────────── */}
      <section className="panel">
        <div className="form">
          {showSteps && (
            <div className="steps">
              <span className="sn">Step {stepIndex + 1} of {stepList.length}</span>
              {stepList.map((s, i) => (
                <span key={s}
                      className={"sd" + (i < stepIndex ? " done" : i === stepIndex ? " now" : "")}>
                  <i />
                </span>
              ))}
            </div>
          )}

          <div className="panes">
            {/* 1 · Mobile */}
            <div {...paneProps("landing")}>
              <h1>Let&apos;s get you in</h1>
              <p className="sub">
                {hostFirst ? <><b>{hostFirst}</b> is waiting. </> : "Your advisor is waiting. "}
                Verify your mobile and you&apos;ll join the room.
              </p>
              <label className="lbl" htmlFor="grj-ph">Mobile number</label>
              <div className="field">
                <span className="cc">{countryCode}</span>
                <input id="grj-ph" type="tel" inputMode="numeric" maxLength={10}
                       placeholder="98765 43210" autoComplete="tel" value={mobile}
                       onChange={(e) => { setMobile(e.target.value.replace(/\D/g, "")); if (err) setErr(""); }}
                       onKeyDown={(e) => { if (e.key === "Enter" && digits.length === 10) void sendOtp(); }} />
              </div>
              {err && stage === "landing" && <p className="err">{err}</p>}
              <button className="btn" disabled={busy || digits.length !== 10}
                      onClick={() => void sendOtp()}>
                {busy ? "Sending…" : "Send verification code"}
                {!busy && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                )}
              </button>
              <p className="note">
                We use your number only to confirm it&apos;s you and to let {hostLabel} follow
                up after the tour.
              </p>
              {recordingNotice}
              {joinPrep}
            </div>

            {/* 1g · Family pass — no OTP, no lead, no lobby.
                A pass arrives as a bare WhatsApp link with no context, so
                WHO vouched for you is the most important thing on the page
                after the name field. */}
            <div {...paneProps("guest")}>
              <div className="invite">
                <span className="inv-av">{initialsOf(inviter || "Guest")}</span>
                <div className="inv-i">
                  <b>{inviter ? `${inviter} invited you` : "You've been invited"}</b>
                  <small>You&apos;re joining as family</small>
                </div>
                <span className="inv-pill">Pass</span>
              </div>

              <h1>What should we call you?</h1>
              <p className="sub">
                {/* Promise only what is true whether or not the host has to
                    let them in: on stage today a family pass still queues
                    in the lobby (backend item 31), and "you'll go straight
                    in" then leaves a grandmother watching a spinner she was
                    told she'd skip. */}
                That&apos;s all we need — no number, no code. {inviter ? `${inviter}'s` : "Your"} invite
                covers you.
              </p>
              <label className="lbl" htmlFor="grj-gname">Your first name</label>
              <div className="field">
                <input id="grj-gname" value={firstName} placeholder="Meera" maxLength={30}
                       autoComplete="given-name"
                       onChange={(e) => { setFirstName(e.target.value); if (err) setErr(""); }}
                       onKeyDown={(e) => { if (e.key === "Enter" && firstName.trim()) void joinAsGuest(); }} />
              </div>
              {err && stage === "landing" && <p className="err">{err}</p>}
              <button className="btn" disabled={busy || !firstName.trim()}
                      onClick={() => void joinAsGuest()}>
                {busy ? "Joining…" : "Join the tour"}
              </button>
              {/* Collect NOTHING else. No mobile, no email, no relationship
                  field — every extra field costs attendance, and the lead
                  already vouched for this person. */}
              <p className="note">
                No number, no OTP, no account.{" "}
                {inviter ? `${inviter}'s pass covers you` : "Their pass covers you"} — we only
                show your name to the others in the room.
              </p>
              {/* The family door needs this as much as the lead door: someone
                  arriving on a forwarded pass has agreed to nothing at all. */}
              {recordingNotice}
            </div>

            {/* 2g · Entering. Not a queue: the pass IS the approval. */}
            <div {...paneProps("entering")}>
              <div className="orbit" aria-hidden="true">
                <i /><i /><i />
                <span className="orbit-c"><CamIcon /></span>
              </div>
              <h1>{passQueued ? "Almost there" : "Taking you in…"}</h1>
              <p className="sub">
                {passQueued
                  ? `${HostLabelCap} is just finishing up — you'll be taken in automatically. Keep this tab open.`
                  : "You'll drop straight into the walkthrough, wherever the tour has got to."}
              </p>
              <div className="tl">
                <div className="tl-i done">
                  <span className="tl-d"><TickIcon /></span>
                  <div>
                    <div className="tl-t">Pass accepted</div>
                    {firstName.trim() && <div className="tl-s">{firstName.trim()}</div>}
                  </div>
                </div>
                <div className="tl-i done">
                  <span className="tl-d"><TickIcon /></span>
                  <div>
                    <div className="tl-t">{inviter ? `${inviter} notified` : "Your host is notified"}</div>
                    <div className="tl-s">
                      {inviter ? "They can see you're on the way" : "They can see you're on the way"}
                    </div>
                  </div>
                </div>
                <div className="tl-i now">
                  <span className="tl-d"><i /></span>
                  <div>
                    <div className="tl-t">Loading the walkthrough</div>
                    <div className="tl-s">{passQueued ? "Any moment now" : "A few seconds"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 1b · Details — unknown numbers only, BEFORE the code.
                Framed as a first visit, not as a failed lookup: "we don't
                have you on record" reads like a rejection at the exact
                moment someone is trying to join. The number is confirmed
                lower down rather than led with. */}
            <div {...paneProps("details")} className={`${pane("details")} pane-details`}>
              <h1>Tell us who you are</h1>
              <p className="sub">
                Looks like this is your first tour with us. A couple of details so{" "}
                {/* Never a gendered pronoun: a first name says nothing about
                    it, and this read "he's" for every advisor. */}
                {hostFirst || "your advisor"} knows who they&apos;re showing around — and can
                follow up after.
              </p>
              <div className="two">
                <div className="fg">
                  <label className="lbl" htmlFor="grj-fn">First name</label>
                  <div className="field">
                    <input id="grj-fn" type="text" placeholder="Rahul" autoComplete="given-name"
                           value={firstName}
                           onChange={(e) => { setFirstName(e.target.value); if (err) setErr(""); }} />
                  </div>
                </div>
                <div className="fg">
                  <label className="lbl" htmlFor="grj-ln">Last name</label>
                  <div className="field">
                    <input id="grj-ln" type="text" placeholder="Verma" autoComplete="family-name"
                           value={lastName}
                           onChange={(e) => { setLastName(e.target.value); if (err) setErr(""); }} />
                  </div>
                </div>
              </div>
              <div className="fg">
                <label className="lbl" htmlFor="grj-em">Email</label>
                <div className="field">
                  <input id="grj-em" type="email" placeholder="rahul@email.com" autoComplete="email"
                         value={email}
                         onChange={(e) => { setEmail(e.target.value); if (err) setErr(""); }}
                         onKeyDown={(e) => { if (e.key === "Enter" && detailsValid) void submitDetails(); }} />
                </div>
              </div>
              {err && stage === "details" && <p className="err">{err}</p>}
              <button className="btn" disabled={busy || !detailsValid}
                      onClick={() => void submitDetails()}>
                {busy ? "Sending…" : "Send verification code"}
                {!busy && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                )}
              </button>
              <p className="note">
                For <b>{countryCode} {prettyMobile(mobile)}</b> ·{" "}
                <button className="link"
                        onClick={() => { setStage("landing"); setDetailsRequired(false); setErr(""); }}>
                  Change number
                </button>
                <br />Used only for this tour and the follow-up.
              </p>
            </div>

            {/* 2 · Code */}
            <div {...paneProps("otp")}>
              <h1>Enter your code</h1>
              <p className="sub">
                A 6-digit code just went to <b>{countryCode} {prettyMobile(mobile)}</b>.{" "}
                <button className="link"
                        onClick={() => { setStage("landing"); clearOtp(); setErr(""); }}>
                  Change number
                </button>
              </p>
              <div className={"otp" + (shake ? " bad" : "")}>
                {Array.from({ length: OTP_LEN }, (_, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el; }}
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    aria-label={`Digit ${i + 1}`}
                    className={otpArr[i] ? "filled" : ""}
                    value={otpArr[i] || ""}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => onOtpKeyDown(i, e)}
                    onPaste={onOtpPaste}
                  />
                ))}
              </div>
              {err && stage === "otp" && <p className="err">{err}</p>}
              <div className="meta">
                <span>{resendIn > 0 ? `Resend in 0:${String(resendIn).padStart(2, "0")}` : "Didn't get it?"}</span>
                <button className="link" disabled={busy || resendIn > 0}
                        onClick={() => void sendOtp()}>
                  Resend code
                </button>
              </div>
              <button className="btn" disabled={busy || !otpComplete}
                      onClick={() => void submitOtp()}>
                {busy ? "Verifying…" : "Verify & enter lobby"}
              </button>
            </div>

            {/* 3 · Details — new lead only. Asked AFTER verification so we
                only collect personal details from someone who has proved
                the number is theirs. */}
            <div {...paneProps("profile")}>
              <h1>A few quick details</h1>
              <p className="sub">
                So {hostLabel} knows who&apos;s joining and can follow up after the tour.
              </p>
              {/* Distinct ids from the details pane above — every pane is
                  in the DOM at once, so `grj-fn` twice would give two
                  labels the same target. */}
              <label className="lbl" htmlFor="grj-pfn">First name</label>
              <div className="field">
                <input id="grj-pfn" value={firstName} placeholder="Meera"
                       onChange={(e) => { setFirstName(e.target.value); if (err) setErr(""); }} />
              </div>
              <label className="lbl sp" htmlFor="grj-pln">Last name</label>
              <div className="field">
                <input id="grj-pln" value={lastName} placeholder="Rao"
                       onChange={(e) => setLastName(e.target.value)} />
              </div>
              <label className="lbl sp" htmlFor="grj-pem">Email</label>
              <div className="field">
                <input id="grj-pem" type="email" value={email} placeholder="meera@example.com"
                       onChange={(e) => setEmail(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") void submitProfile(); }} />
              </div>
              {err && stage === "profile" && <p className="err">{err}</p>}
              <button className="btn" disabled={busy || !firstName.trim()}
                      onClick={() => void submitProfile()}>
                {busy ? "Saving…" : "Continue"}
              </button>
              <p className="note">
                {HostLabelCap} will use these only to contact you about this project.
              </p>
            </div>

            {/* 4 · Lobby */}
            <div {...paneProps("lobby")}>
              <div className="orbit" aria-hidden="true">
                <i /><i /><i />
                <span className="orbit-c"><CamIcon /></span>
              </div>
              <h1>You&apos;re in the lobby</h1>
              <p className="sub">
                {HostLabelCap} will start the walkthrough shortly. Keep this tab open —
                you&apos;ll be taken in automatically.
              </p>
              <div className="tl">
                <div className="tl-i done">
                  <span className="tl-d"><TickIcon /></span>
                  <div>
                    <div className="tl-t">{isGuest ? "You're checked in" : "Mobile verified"}</div>
                    {!isGuest && mobile && (
                      <div className="tl-s">{countryCode} {prettyMobile(mobile)}</div>
                    )}
                  </div>
                </div>
                <div className="tl-i done">
                  <span className="tl-d"><TickIcon /></span>
                  <div>
                    <div className="tl-t">{HostLabelCap} notified</div>
                    <div className="tl-s">You&apos;re visible in the lobby</div>
                  </div>
                </div>
                <div className="tl-i now">
                  <span className="tl-d"><i /></span>
                  <div>
                    <div className="tl-t">Waiting to be let in</div>
                    <div className="tl-s">Usually under a minute</div>
                  </div>
                </div>
                <div className="tl-i">
                  <span className="tl-d" />
                  <div><div className="tl-t">Walkthrough begins</div></div>
                </div>
              </div>
            </div>

            {/* 4b · Admitted. Two readings: in and waiting for the host to
                start, or in and the tour is running. */}
            <div {...paneProps("admitted")}>
              {roomLive === false ? (
                <>
                  <div className="orbit" aria-hidden="true">
                    <i /><i /><i />
                    <span className="orbit-c"><CamIcon /></span>
                  </div>
                  <h1>{HostLabelCap} will be with you shortly</h1>
                  <p className="sub">
                    You&apos;re in — the tour starts as soon as they join. Keep this tab open.
                  </p>
                  <div className="tl">
                    <div className="tl-i done">
                      <span className="tl-d"><TickIcon /></span>
                      <div><div className="tl-t">You&apos;re in the room</div></div>
                    </div>
                    <div className="tl-i now">
                      <span className="tl-d"><i /></span>
                      <div>
                        <div className="tl-t">Waiting for {hostLabel}</div>
                        <div className="tl-s">You&apos;ll be taken in automatically</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="orbit" aria-hidden="true">
                    <i /><i /><i />
                    <span className="orbit-c"><CamIcon /></span>
                  </div>
                  <h1>You&apos;re in</h1>
                  <p className="sub">
                    {voice.status === "live"
                      ? "You can hear the tour. Keep this tab open."
                      : voice.status === "connecting"
                        ? "Connecting you to the tour…"
                        : "The tour is starting. If nothing loads in a moment, refresh the page."}
                  </p>
                  {/* Voice is live; the synced walkthrough canvas mounts here
                      once ws_token can mint (the backend's signing key isn't
                      set yet). A buyer is a subscriber, so there is no mic
                      control — they listen. Deliberate for v1: a household
                      on speakerphone talking over the host is the failure
                      mode, and it's much easier to add talk-back later than
                      to take it away. */}
                  {voice.status === "error" && voice.error && (
                    <p className="sub err" role="status">{voice.error}</p>
                  )}
                </>
              )}
            </div>

            {/* Family pass fully used. Deliberately not phrased as a
                failure — nobody did anything wrong, the household simply
                filled its four places, and the person reading this is
                most likely a relative rather than a stranger. */}
            <div {...paneProps("passfull")}>
              {/* "This tour is full" was wrong, and wrong in the direction
                  that turns someone away: the TOUR has room — the room
                  ceiling is separate and much higher — it is this one
                  family link that is used up. A relative told the tour is
                  full stops trying; a relative told the link is full asks
                  for another way in, which is the outcome we want and the
                  next sentence offers. Caught on a live tour 2026-09-10,
                  where the room held 7 of 8 while this screen claimed to
                  be full. */}
              <h1>This family link is full</h1>
              <p className="sub">
                All {passMax} family places on{" "}
                {inviter ? <b>{inviter}&apos;s</b> : "this"} pass have been taken.
                {inviter ? ` Ask ${inviter} to bring you into the next one — ` : " "}
                {/* Capitalised: with no host name this follows a full stop,
                    and rendered as "…have been taken. your advisor can…". */}
                {hostFirst ? `${hostFirst} can also set up a tour just for you.` : "Your advisor can also set up a tour just for you."}
              </p>
            </div>

            {/* ── A LINK THAT CAN'T BE USED ──
                Used to be a red "That invite link isn't valid any more"
                under the name field — a form error, on the one screen a
                relative sees before being turned away. Founder, 2026-09-11:
                "it should look premium to the user even if we are rejecting
                him". So it gets the same treatment as a full link or an
                ended tour: a calm screen, the home still named on the left,
                no blame, and a real next step. It never says "invalid". */}
            <div {...paneProps("passinvalid")}>
              <h1>This invitation isn&apos;t active</h1>
              <p className="sub">
                {inviter ? <><b>{inviter}&apos;s</b> family link</> : "This family link"} can&apos;t
                be used right now — it may have expired.{" "}
                {inviter ? `Ask ${inviter} to send you a fresh one, or ` : "Ask whoever invited you for a fresh link, or "}
                {hostFirst ? `${hostFirst} can set up a tour just for you.` : "your advisor can set up a tour just for you."}
              </p>
            </div>

            {/* Ended */}
            <div {...paneProps("ended")}>
              <h1>This tour has ended</h1>
              <p className="sub">
                {hostFirst
                  ? `Ask ${hostFirst} for a new link when you're ready to look again.`
                  : "Ask your advisor for a new link when you're ready to look again."}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* Never blame the buyer for a server fault. A 5xx means our side broke —
   telling someone to "check the number" when their number is fine just
   gets them retyping a correct number until they give up.
   Also reads `error_message`, which is the shape this backend returns
   (`detail` is FastAPI's default and only appears on validation errors). */
function friendly(e: any, fallback: string): string {
  const status = e?.response?.status;
  if (typeof status === "number" && status >= 500) {
    return "We couldn't send the code just now. Please try again in a moment.";
  }
  const data = e?.response?.data;
  const msg = data?.error_message || data?.detail;
  if (typeof msg === "string" && msg.length < 140) return msg;
  return fallback;
}

export default GuidedRoomJoin;
export { GuidedRoomJoin };
